import React, { useCallback, useEffect, useRef, useState } from 'react';
import {Button, ButtonGroup, CircularProgress, Grid, IconButton, List, ListItem, ListItemText, Menu, MenuItem, Pagination, Stack, TextField, Typography} from "@mui/material";
import _, { set } from "lodash";
import Div from "@jumbo/shared/Div";
import { ASSET_IMAGES, GOOGLE_MAPS_API_KEY } from 'app/utils/constants/paths';
import { GoogleMap, Marker, OverlayView, Polygon, Polyline, useLoadScript, InfoWindow} from "@react-google-maps/api";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import { convexHull, geoCoords, getAssetPath } from 'app/utils/appHelpers';
import deliveryZoneServices from 'app/services/delivery-zone-services';
import { getCarrierMarkerColor, getDropoffMarkerStyle } from 'app/utils/mapMarkers';

// DrawingManager fue removido de la API de Google Maps en la v3.65, así que el
// dibujo de zonas se implementa manualmente (click a click) más abajo.
const GOOGLE_MAPS_LIBS = ["visualization", "places"]

const ZonesMapEdit = props => {
    const {
        zones,
        currentZone,
        loading,
        mapCenter,
        routesPins,
        routes = [],
        hiddenRoutes = [],
        centres = [],
        showZones,
        preview,
        pickupPoints = [],
        onPickupPointClick = () => {},
        dropoffTrips = []
    } = props;

    const [currPin, setCurrPin] = useState(null);

    const [center, setCenter] = useState(mapCenter);

    // Dibujo manual de polígonos (reemplazo del DrawingManager removido por Google).
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawPath, setDrawPath] = useState([]);

    // const isLoaded = false
    const {isLoaded} = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBS
    });
    const mapRef = useRef(null);
    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    useEffect(() => {
        if(mapCenter) {
            setCenter(mapCenter)
        }
    }, [mapCenter])

    useEffect(() => {
        if(preview) {
            if(preview.lat && preview.lng) {
                setCenter({ lat: _.toNumber(preview.lat), lng: _.toNumber(preview.lng) })
            }
        } else {
            setCenter(mapCenter)
        }
    }, [preview])

    const optionsPoly = {   
        strokeOpacity: 0.6,
        strokeWeight: 4,
        fillOpacity: 1,
        clickable: false,
        draggable: false,
        editable: false,
        visible: true,
        radius: 30000,
        zIndex: 1
    };

    const startDrawing = () => {
        setDrawPath([]);
        setIsDrawing(true);
    };

    const cancelDrawing = () => {
        setIsDrawing(false);
        setDrawPath([]);
        props.onDrawCancel?.();
    };

    const handleMapClick = (e) => {
        if (!isDrawing || !e?.latLng) return;
        setDrawPath((prev) => [...prev, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
    };

    // Cierra el polígono y entrega un overlay compatible con el contrato previo
    // de onOverlayComplete (getPath().getArray() de google.maps.LatLng + setMap).
    const finishDrawing = () => {
        const hull = convexHull(drawPath);
        if (hull.length < 3) return;
        const latLngs = hull.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
        const overlay = {
            getPath: () => ({ getArray: () => latLngs }),
            setMap: () => {},
        };
        props.onOverlayComplete?.({ type: 'polygon', overlay });
        setIsDrawing(false);
        setDrawPath([]);
    };

    useEffect(() => {
        if (!isDrawing) return;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') cancelDrawing();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isDrawing]);

    const onMapDrag = () => {
        if (!mapRef.current) return;
        const newCenter = mapRef.current.getCenter();
        if (!newCenter) return;
        const lat = newCenter.lat();
        const lng = newCenter.lng();
        if (!_.isFinite(lat) || !_.isFinite(lng)) return;
        setCenter({ lat, lng });
    };

    const renderZonesPolygons = () => {
        if(showZones) {
            return _.map(zones, (dz, i) => {
                return (
                    <Polygon
                        path={geoCoords(dz.location?.path)}
                        key={`dz_${i}`}
                        onClick={() => { if (!isDrawing) props.onPolygonClick(dz) }}
                        options={{
                            strokeColor: dz == currentZone ? "#FF0000" : "#0000FF",
                            strokeOpacity: 0.8,
                            strokeWeight: 1,
                            fillColor: dz == currentZone ? "#FF0000" : "#0000FF",
                            fillOpacity: 0.1,
                            clickable: !isDrawing
                        }}
                    />
                )
            })
        }
    }

    const renderCentresPins = () => {
        return centres.map((centre, i) => {
            if(centre) {
                return (
                    <Marker
                        key={`centre_${i}`}
                        position={{ lat: _.toNumber(centre.location?.lat), lng: _.toNumber(centre.location?.lng) }}
                        icon={getAssetPath(`${ASSET_IMAGES}/map-markers/base.png`)}
                        onClick={() => setCurrPin({ id: `centre_${i}` })}
                        // onMouseOver={() => setCurrPin({ id: `${i}_${j}` })}
                        // onMouseOut={() => setCurrPin(null)}
                    >
                        { (currPin && currPin.id === `centre_${i}`) &&
                            <InfoWindow onCloseClick={() => setCurrPin(null)}>
                                <Stack direction="column" spacing={0.5}>
                                    <Typography variant="body2" color="text.secondary" sx={{fontSize: 10}}>{centre.name}</Typography>
                                </Stack>
                            </InfoWindow>
                        }
                    </Marker>
                )
            }
        })
    }

    const renderPins = () => {
        return (
            routesPins.map((route, i) => {
                return route.pins.map((pin, j) => {
                    return (
                        <Marker
                            key={`pin_${i}_${j}`}
                            label={{
                                text: `${j + 1}`,
                                color: 'white',
                                fontSize: '12px'
                            }}
                            position={{ lat: _.toNumber(pin.lat), lng: _.toNumber(pin.lng) }}
                            icon={{
                                path: "M0 -20C-3.87 -20 -7 -16.87 -7 -13c0 5.25 7 13 7 13s7 -7.75 7 -13c0 -3.87 -3.13 -7 -7 -7z",//window.google.maps.SymbolPath.CIRCLE,
                                strokeColor: route.color,
                                fillOpacity: 1,
                                fillColor: route.color,
                                scale: 1.3,
                                labelOrigin: new window.google.maps.Point(0, -12),
                            }}
                            onClick={() => setCurrPin({ id: `${i}_${j}` })}
                            // onMouseOver={() => setCurrPin({ id: `${i}_${j}` })}
                            // onMouseOut={() => setCurrPin(null)}
                        >
                            { (currPin && currPin.id === `${i}_${j}`) &&
                                <InfoWindow onCloseClick={() => setCurrPin(null)}>
                                    <Stack direction="column" spacing={0.5}>
                                        <Typography variant="body2" color="text.secondary" sx={{fontSize: 10}}>{pin.info}</Typography>
                                    </Stack>
                                </InfoWindow>
                            }
                        </Marker>
                    )
                })
            })
        )
    }

    const renderPreviewPin = () => {
        if(preview && preview?.lat && preview?.lng) {
            return (
                <Marker
                    key={`preview_pin`}
                    label={{
                        text: `i`,
                        color: 'white',
                        fontSize: '14px'
                    }}
                    position={{ lat: _.toNumber(preview.lat), lng: _.toNumber(preview.lng) }}
                    icon={{
                        path: "M0 -20C-3.87 -20 -7 -16.87 -7 -13c0 5.25 7 13 7 13s7 -7.75 7 -13c0 -3.87 -3.13 -7 -7 -7z",//window.google.maps.SymbolPath.CIRCLE,
                        strokeColor: 'blue',
                        fillOpacity: 1,
                        fillColor: 'blue',
                        scale: 1.3,
                        labelOrigin: new window.google.maps.Point(0, -12),
                    }}
                >
                    <InfoWindow>
                        <Stack direction="column" spacing={0.5}>
                            <Typography variant="body2" color="text.secondary" sx={{fontSize: 10}}>{preview.title}</Typography>
                        </Stack>
                    </InfoWindow>
                </Marker>
            )
        }
    }

    // Las lineas que unian las paradas quedan ocultas a pedido: se muestran solo los pines
    // numerados, que ya llevan el color de su ruta y alcanzan para leer el orden.
    //
    // Se apaga con una constante en vez de borrar `renderRoutes` porque el trazado sigue
    // llegando en `routes` y volver a mostrarlo es cambiar este false por true.
    const SHOW_ROUTE_PATHS = false;

    const renderRoutes = () => {
        return (
            routes.map((route, i) => {
                if(hiddenRoutes.includes(i)) return null
                return (
                    route.paths.map((path, j) => {
                        if(!routesPins[i]?.color) return null
                        return (
                            <Polyline
                                // onLoad={onLoadPoly}
                                key={`route_${i}_${j}`}
                                path={path}
                                options={{...optionsPoly, strokeColor: routesPins[i]?.color}}
                            />
                        )
                    })
                )
            })
        )
    }

    const renderInfoWindow = () => {
        if(currentZone && currentZone.location?.path) {
            var fp = currentZone.location.path.split(';')[0]?.split(',')
            if(fp.length > 0) {
                return (
                    <InfoWindow
                        position={{ lat: _.toNumber(fp[0]), lng: _.toNumber(fp[1]) }}
                        onCloseClick={props.onCloseInfoWindow}
                    >
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                error={false}
                                variant="standard"
                                value={currentZone.name}
                                onChange={props.onZoneNameChange}
                            />
                            <ButtonGroup variant="outlined" size="small">
                                <IconButton onClick={props.onSaveZoneClick} color="success"><SaveOutlinedIcon/></IconButton>
                                <TrashIconButton title="Eliminar zona" onClick={props.onDeleteZoneClick} />
                            </ButtonGroup>
                        </Stack>
                    </InfoWindow>
                )
            }
        }
    }

    const renderPickupPoints = () => {
        if (!pickupPoints || pickupPoints.length === 0) return null;

        return pickupPoints.map((point) => {
            const color = getCarrierMarkerColor(point.carrier);

            return (
                <Marker
                    key={`pickup_${point.id}`}
                    position={{
                        lat: _.toNumber(point.lat),
                        lng: _.toNumber(point.lng),
                    }}
                    icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 7,
                        fillColor: color,
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                    }}
                    onClick={() => onPickupPointClick(point)} 
                >
                    {currPin?.id === `pickup_${point.id}` && (
                        <InfoWindow onCloseClick={() => setCurrPin(null)}>
                            <Stack direction="column" spacing={0.5}>
                                <Typography variant="body2" sx={{ fontSize: 11 }}>
                                    {point.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {point.address_hint}
                                </Typography>
                            </Stack>
                        </InfoWindow>
                    )}
                </Marker>
            );
        });
    };

    const renderDropoffMarkers = () => {
        if (!dropoffTrips || dropoffTrips.length === 0) return null;

        const markers = [];

        dropoffTrips.forEach((trip) => {
            trip.orders?.forEach((order) => {
                if (
                    order.dropoff &&
                    order.dropoff_point?.lat &&
                    order.dropoff_point?.lng
                ) {
                    markers.push(
                        <Marker
                            key={`dropoff_${order.id}`}
                            position={{
                                lat: _.toNumber(order.dropoff_point.lat),
                                lng: _.toNumber(order.dropoff_point.lng),
                            }}
                            icon={getDropoffMarkerStyle({
                                carrier: order.dropoff_carrier,
                                state: order.state,
                                pointFactory: window.google?.maps?.Point
                                    ? (x, y) => new window.google.maps.Point(x, y)
                                    : undefined,
                            })}
                            zIndex={999}
                        />
                    );
                }
            });
        });

        return markers;
    };

    // Render del polígono en construcción mientras el usuario dibuja.
    const renderDrawingOverlay = () => {
        if (!isDrawing || drawPath.length === 0) return null;
        return (
            <React.Fragment>
                {drawPath.length >= 3 && (
                    <Polygon
                        path={convexHull(drawPath)}
                        options={{
                            fillColor: "#199ee0",
                            fillOpacity: 0.2,
                            strokeColor: "#113460",
                            strokeWeight: 2,
                            clickable: false,
                            zIndex: 2
                        }}
                    />
                )}
                <Polyline
                    path={drawPath}
                    options={{ strokeColor: "#113460", strokeWeight: 1, strokeOpacity: 0.4, clickable: false, zIndex: 3 }}
                />
                {drawPath.map((pt, idx) => (
                    <Marker
                        key={`draw_vertex_${idx}`}
                        position={pt}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 5,
                            fillColor: idx === 0 ? "#ff5252" : "#113460",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 1,
                        }}
                        zIndex={4}
                        onClick={() => { if (idx === 0 && drawPath.length >= 3) finishDrawing(); }}
                    />
                ))}
            </React.Fragment>
        );
    };

    return (
        <React.Fragment>
            { isLoaded ?
                <Div sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <GoogleMap mapContainerStyle={{ width: '100%', height: "100%", borderRadius: 12 }}
                        center={center}
                        onLoad={onMapLoad}
                        onDragEnd={onMapDrag}
                        onClick={handleMapClick}
                        options={{ draggableCursor: isDrawing ? 'crosshair' : undefined }}
                        zoom={10}>
                        {
                            <React.Fragment>
                                {renderCentresPins()}
                                {SHOW_ROUTE_PATHS && renderRoutes()}
                                {renderPins()}
                                {renderZonesPolygons()}
                                {renderInfoWindow()}
                                {renderPreviewPin()}
                                {renderPickupPoints()}
                                {renderDropoffMarkers()}
                                {renderDrawingOverlay()}
                            </React.Fragment>
                        }
                    </GoogleMap>

                    <Div sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 5, display: 'flex', gap: 1, alignItems: 'center' }}>
                        { !isDrawing ?
                            <Button size="small" variant="contained" startIcon={<HexagonOutlinedIcon />} onClick={startDrawing}>
                                Dibujar zona
                            </Button>
                            :
                            <React.Fragment>
                                <Button size="small" variant="contained" color="success" disabled={drawPath.length < 3} onClick={finishDrawing}>
                                    Finalizar ({drawPath.length})
                                </Button>
                                <Button size="small" variant="contained" color="error" onClick={cancelDrawing}>
                                    Cancelar
                                </Button>
                            </React.Fragment>
                        }
                    </Div>
                </Div>

                :
                <Div sx={{textAlign: 'center', my: 5}}>
                    <CircularProgress color="warning"/>
                </Div>
            }
        </React.Fragment>
    )
}

export default ZonesMapEdit;
